<template lang="pug">
.scan-events
    .page-loading(v-if="loading")
        el-skeleton(:rows="4" animated)

    el-empty(
        v-else-if="!events.length"
        :description="$t('Keine Events gefunden')"
    )
        svg.scan-empty-icon(
            slot="image"
            xmlns="http://www.w3.org/2000/svg"
            width="64" height="64" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round"
        )
            path(d="M8 2v4")
            path(d="M16 2v4")
            path(d="M21 13V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8")
            path(d="M3 10h18")
            path(d="m17 22 5-5")
            path(d="m17 17 5 5")

    template(v-else)
        el-card.event-card(
            v-for="event in sortedEvents"
            :key="event.id"
            :class="{ past: isPast(event) }"
            shadow="hover"
            @click.native="goToScanner(event)"
        )
            .event-card-inner
                .event-info
                    .event-name {{ event.name }}
                    span.event-category(v-if="event.category") {{ event.category.name }}
                    .event-date(v-if="eventDate(event)")
                        i.el-icon-fa-calendar-alt.far
                        |  {{ eventDate(event) }}
                    .event-meta
                        span {{ event.slots.length }} {{ event.slots.length === 1 ? $t('Slot') : $t('Slots') }}
                        span.sep ·
                        span {{ totalBookings(event) }} {{ $t('Anmeldungen') }}
                .event-banner(v-if="bannerSrc(event)")
                    img(:src="bannerSrc(event)" :alt="event.name")
                i.el-icon-arrow-right.arrow
</template>

<i18n>
{
  "de": {
    "Keine Events gefunden": "Keine Events gefunden",
    "Slot": "Slot",
    "Slots": "Slots",
    "Anmeldungen": "Anmeldungen"
  }
}
</i18n>

<script>
import _ from 'lodash'
import { useSetupStore } from '@/stores'
import { timestamp2date } from '@/filters'
import { getFitStringFromProcessObject } from '@/helpers'
import EventApi from '@/extensions/eventManager/store/api/event'

export default {
    name: 'ScanEvents',
    data() {
        return {
            events: [],
            loading: true,
        }
    },
    computed: {
        cmsId() { return useSetupStore().activeCms?.id },
        // Aktuelle Termine oben: laufende/kommende Events zuerst (naechster oben),
        // bereits vergangene danach (zuletzt gewesene zuerst)
        sortedEvents() {
            const start = e => {
                const s = (e.slots || []).map(x => x.start_at).filter(Boolean)
                return s.length ? Math.min(...s) : Infinity
            }
            return [...this.events].sort((a, b) => {
                const aPast = this.isPast(a)
                const bPast = this.isPast(b)
                if (aPast !== bPast) return aPast ? 1 : -1
                return aPast ? start(b) - start(a) : start(a) - start(b)
            })
        },
    },
    mounted() {
        this.loadEvents()
    },
    methods: {
        async loadEvents() {
            this.loading = true
            try {
                const events = await EventApi.fetchList(this.cmsId)
                this.events = (events || []).filter(e => e.booking !== 'none')
            } catch {
                this.events = []
            } finally {
                this.loading = false
            }
        },
        totalBookings(event) {
            return (event.slots || []).reduce((sum, s) => sum + (s.bookings?.length || 0), 0)
        },
        // spaetester Slot-Zeitpunkt des Events (oder null ohne Slots)
        eventEnd(event) {
            const ends = (event.slots || []).map(s => s.end_at || s.start_at).filter(Boolean)
            return ends.length ? Math.max(...ends) : null
        },
        // Event ist abgelaufen, wenn alle Slots vorbei sind
        isPast(event) {
            const end = this.eventEnd(event)
            return end !== null && end < Date.now() / 1000
        },
        // Datum bzw. Datumsspanne aus den Slots ableiten (Event hat kein eigenes Datum)
        eventDate(event) {
            const starts = (event.slots || []).map(s => s.start_at).filter(Boolean)
            if (!starts.length) return null
            const min = Math.min(...starts)
            const ends = (event.slots || []).map(s => s.end_at || s.start_at).filter(Boolean)
            const max = Math.max(...ends, min)
            const startDay = timestamp2date(min, 'dd.MM.yyyy')
            const endDay = timestamp2date(max, 'dd.MM.yyyy')
            return startDay === endDay ? startDay : `${timestamp2date(min, 'dd.MM.')} – ${endDay}`
        },
        // Banner-Vorschau (16:9). Bei vorhandenem Zuschnitt die FileController-Route bauen
        // (wendet fit/crop an – banner.url kann eine direkte Storage-URL sein), sonst das
        // Original (der 16:9-Rahmen zentriert via object-fit). Muster wie im Banner-Editor.
        bannerSrc(event) {
            const banner = _.get(event, 'banner')
            const url = _.get(banner, 'url')
            if (!url) return ''
            const processing = _.get(banner, 'settings.processing')
            if (!processing) return url
            const query = getFitStringFromProcessObject(processing)
            if (!query) return url
            const route = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const table = banner.upload ? 'uploads' : 'images'
            return `${route}/${table}/${banner.id}/${banner.name}?${query}`
        },
        goToScanner(event) {
            this.$router.push({ name: 'scan-scanner', params: { id: event.id } })
        },
    },
}
</script>

<style lang="sass" scoped>
.scan-events
    flex: 1
    min-height: 0
    overflow-y: auto
    width: 100%
    padding: 16px
    max-width: 480px
    margin: 0 auto

.page-loading
    padding: 16px
    ::v-deep
        .el-skeleton__item
            background: #1a1a1a

.event-card
    margin-bottom: 12px
    border-radius: 10px
    cursor: pointer
    -webkit-tap-highlight-color: transparent

    &.past
        opacity: 0.5

    ::v-deep
        &.el-card
            background: #1a1a1a
            border-color: #2a2a2a
            &:hover
                border-color: $--color-primary
        .el-card__body
            padding: 0

    .event-card-inner
        display: flex
        align-items: center
        padding: 16px
        gap: 12px

    .event-info
        flex: 1
        min-width: 0

    .event-name
        font-size: 16px
        font-weight: 600
        color: #d0d0d0
        margin-bottom: 6px
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

    .event-category
        display: inline-block
        font-size: 12px
        color: $--color-primary
        margin-bottom: 6px

    .event-date
        font-size: 13px
        color: #aaa
        margin-bottom: 6px
        i
            color: $--color-primary
            margin-right: 6px

    .event-meta
        font-size: 12px
        color: #555
        .sep
            margin: 0 6px

    .event-banner
        flex-shrink: 0
        width: 96px
        aspect-ratio: 16 / 9
        border-radius: 8px
        overflow: hidden
        background: #0f0f0f
        border: 1px solid #2a2a2a
        img
            width: 100%
            height: 100%
            object-fit: cover
            display: block

    .arrow
        color: #333
        font-size: 14px
        flex-shrink: 0

::v-deep .el-empty
    &__description p
        color: #666

.scan-empty-icon
    color: #c0c4cc
    display: block
    fill: none
</style>
